import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { useUIStore } from '@/store/useUIStore';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import { useReflashingStore } from '@/store/useReflashingStore';
import {
   reflashStart,
   reflashFinish,
   fetchRouterScript,
   applyRouterConfig,
   pushLoginHtml,
   reflashReplayRun,
   reflashReplayStatus,
   type RouterScriptResponse,
   type ReflashFinishResponse,
} from '@/api/vendo';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
import type { VendoDevice } from '@/pages/Admin/Vendo';

/**
 * Reflash Router — re-onboard an existing main vendo onto a fresh or
 * factory-reset router. Reuses the wizard's setup script + the server's
 * reflash start/finish. The server decides RESTORE (same router, serial
 * unchanged → license never moved) vs REPLACE (new router → license re-keyed).
 *
 * Steps: intro → paste (show bootstrap) → waiting (poll finish until the
 * router's REST is reachable) → done.
 */

type Phase =
   | 'intro'
   | 'paste'
   | 'waiting'
   | 'configuring'
   | 'replaying'
   | 'done'
   | 'error';
const MAX_TRIES = 30; // ~2.5 min at 5s

const MODEL_OPTIONS = [
   { value: 'hex-rb750gr3', label: 'MikroTik hEX RB750Gr3 — up to 20 sub-vendos' },
   { value: 'rb4011', label: 'MikroTik RB4011 — up to 100 sub-vendos' },
   { value: 'ccr2004', label: 'MikroTik CCR2004 — up to 500 sub-vendos' },
   { value: 'ccr2116', label: 'MikroTik CCR2116 — up to 500 sub-vendos' },
];

const ReflashRouter: React.FC<{ data: { device: VendoDevice } }> = ({ data }) => {
   const device = data.device;
   const { handleClose } = useUIStore();
   const refetchDevices = useVendoDevicesStore((s) => s.fetch);
   const [phase, setPhase] = useState<Phase>('intro');
   const [script, setScript] = useState<RouterScriptResponse | null>(null);
   const [result, setResult] = useState<ReflashFinishResponse | null>(null);
   const [errMsg, setErrMsg] = useState('');
   const [tries, setTries] = useState(0);
   const [replay, setReplay] = useState<{ total: number; done: number }>({
      total: 0,
      done: 0,
   });
   const [configPct, setConfigPct] = useState(0);
   const markReflashing = useReflashingStore((s) => s.mark);
   // Operator can upgrade to a different MikroTik model here; defaults to the
   // device's current model (a same-model reflash is unchanged).
   const [model, setModel] = useState<string>(device.model || 'hex-rb750gr3');
   const modelChanged = model !== (device.model || 'hex-rb750gr3');

   // Poll the server's replay job (re-granting live customer sessions on the
   // fresh router). The job runs server-side and is retried on every router
   // heartbeat, so even if this poll gives up the replay still completes.
   const pollReplay = async () => {
      for (let i = 0; i < 100; i++) {
         try {
            const s = await reflashReplayStatus(device._id);
            setReplay({ total: s.total, done: s.done });
            if (s.status === 'done') {
               setPhase('done');
               return;
            }
         } catch {
            // transient — keep polling
         }
         // A config push restarts the router's REST for 1–2 min, so the first
         // replay attempts time out. Keep re-kicking until the REST is back and
         // the sessions land — don't rely on a heartbeat that may be sparse.
         if (i % 4 === 3) {
            try {
               await reflashReplayRun(device._id);
            } catch {
               // ignore — next tick retries
            }
         }
         await sleep(3000);
      }
      setPhase('done'); // replay keeps finishing server-side via heartbeats
   };

   const copy = (text: string, label: string) =>
      navigator.clipboard
         .writeText(text)
         .then(() => toast.success(`${label} copied`, { autoClose: 1500 }))
         .catch(() => toast.error(`Could not copy ${label}`));

   const begin = async () => {
      setPhase('paste');
      try {
         await reflashStart(device._id, model);
         const s = await fetchRouterScript(device._id);
         setScript(s);
      } catch (err) {
         setErrMsg(apiErr(err, 'Could not start reflash'));
         setPhase('error');
      }
   };

   // Poll finish: 409 means the router isn't reachable yet (still booting /
   // connecting), so wait and retry. Any 200 ends it.
   const waitForRouter = async (attempt = 0) => {
      setPhase('waiting');
      setTries(attempt + 1);
      try {
         const res = await reflashFinish(device._id);
         // Mark it reflashing NOW so the device card shows the "Setting up"
         // banner immediately — the device-list refresh fails while the router
         // restarts, so we can't rely on it to surface the state.
         markReflashing(device._id);
         // The bootstrap only brought up the tunnel + REST. Now deliver the FULL
         // config over the tunnel — hotspot, DHCP, walled-garden — which is what
         // lets customers get an IP and the NodeMCU heartbeat. Retry: the router
         // just rebooted, so its REST lags a few seconds behind the serial read.
         setPhase('configuring');
         setConfigPct(5);
         // The router restarts its REST/www for 1-2 min after the first config
         // chunk lands, so early attempts fail mid-apply (and a single attempt
         // can hang for a while). Drive the bar by ELAPSED TIME so it keeps
         // moving even while one attempt is in flight; jump to 100% on success.
         let configured = false;
         const cfgStart = Date.now();
         const cfgTimer = window.setInterval(() => {
            setConfigPct(
               Math.min(90, 5 + Math.round(((Date.now() - cfgStart) / 120000) * 85))
            );
         }, 1000);
         try {
            for (let i = 0; i < 30 && !configured; i++) {
               try {
                  await applyRouterConfig(device._id, { wait: true });
                  configured = true;
               } catch {
                  if (i < 29) await sleep(5000);
               }
            }
         } finally {
            window.clearInterval(cfgTimer);
         }
         setConfigPct(100);
         if (!configured) {
            toast.warn(
               'Router linked, but the full config push kept failing — re-apply from Device Settings (the portal/DHCP may not work yet).'
            );
         }
         // Captive portal page (login.html), after the hotspot exists.
         let pushed = false;
         for (let i = 0; i < 4 && !pushed; i++) {
            try {
               await pushLoginHtml(device._id);
               pushed = true;
            } catch {
               if (i < 3) await sleep(4000);
            }
         }
         if (!pushed) {
            toast.warn(
               'Router linked, but the portal page (login.html) did not push — re-push from Device Settings if the portal looks wrong.'
            );
         }
         setResult(res);
         refetchDevices();
         // Config is live → re-grant the live customer sessions and show progress.
         const total = res.sessionReplay?.total ?? 0;
         if (total > 0) {
            setReplay({ total, done: 0 });
            setPhase('replaying');
            try {
               await reflashReplayRun(device._id);
            } catch {
               // heartbeat will still drive it
            }
            void pollReplay();
         } else {
            setPhase('done');
         }
      } catch (err) {
         const status = (err as { response?: { status?: number } })?.response
            ?.status;
         if (status === 409 && attempt + 1 < MAX_TRIES) {
            setTimeout(() => waitForRouter(attempt + 1), 5000);
            return;
         }
         setErrMsg(
            status === 409
               ? "The router never came online. Check it's powered, connected to the internet, and that you pasted the whole script — then try again."
               : apiErr(err, 'Reflash failed')
         );
         setPhase('error');
      }
   };

   return (
      <Wrap>
         {phase === 'intro' && (
            <>
               <Lead>
                  Reflash puts a router back online for <b>{device.name}</b> —
                  after you <b>factory-reset</b> this router, or when you swap in
                  a <b>new</b> one. Your license and remaining time stay with you:
                  the server links whichever router connects.
               </Lead>
               <Note>
                  ⚠ Factory-reset the router first, and have it connected to the
                  internet. The old router (if you're replacing it) can be set
                  aside.
               </Note>
               <FieldLabel>Router model</FieldLabel>
               <ModelSelect
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
               >
                  {MODEL_OPTIONS.map((o) => (
                     <option key={o.value} value={o.value}>
                        {o.label}
                        {o.value === (device.model || 'hex-rb750gr3')
                           ? ' (current)'
                           : ''}
                     </option>
                  ))}
               </ModelSelect>
               {modelChanged && (
                  <Muted style={{ textAlign: 'left' }}>
                     Upgrading to a different model — the config will be rebuilt
                     for its ports. Plug your internet into <b>ether1</b> on the
                     new router.
                  </Muted>
               )}
               <Actions>
                  <Ghost onClick={handleClose}>Cancel</Ghost>
                  <Primary onClick={begin}>Start</Primary>
               </Actions>
            </>
         )}

         {phase === 'paste' && (
            <>
               <Lead>
                  Open the router's terminal and paste the line
                  {script?.bootstrapImport ? 's' : ''} below. The router fetches
                  its config and reboots.
               </Lead>
               {!script ? (
                  <Muted>Preparing setup script…</Muted>
               ) : (
                  <>
                     <PasteBlock>
                        <PasteLabel>1 · Paste this first</PasteLabel>
                        <Code>{script.bootstrap}</Code>
                        <CopyBtn onClick={() => copy(script.bootstrap, 'Line 1')}>
                           Copy
                        </CopyBtn>
                     </PasteBlock>
                     {script.bootstrapImport && (
                        <PasteBlock>
                           <PasteLabel>
                              2 · After line 1 finishes, paste this
                           </PasteLabel>
                           <Code>{script.bootstrapImport}</Code>
                           <CopyBtn
                              onClick={() =>
                                 copy(script.bootstrapImport!, 'Line 2')
                              }
                           >
                              Copy
                           </CopyBtn>
                        </PasteBlock>
                     )}
                     <Actions>
                        <Ghost onClick={handleClose}>Cancel</Ghost>
                        <Primary onClick={() => waitForRouter(0)}>
                           I've pasted it — check connection
                        </Primary>
                     </Actions>
                  </>
               )}
            </>
         )}

         {phase === 'waiting' && (
            <Center>
               <Spinner />
               <Lead style={{ textAlign: 'center' }}>
                  Waiting for the router to come online…
               </Lead>
               <Muted>
                  This can take 1–2 minutes while it reboots and connects
                  (attempt {tries}/{MAX_TRIES}). Keep this window open.
               </Muted>
            </Center>
         )}

         {phase === 'configuring' && (
            <Center>
               <Lead style={{ textAlign: 'center', marginBottom: 4 }}>
                  Setting up the router…
               </Lead>
               <Muted>
                  Pushing the hotspot, DHCP, and portal so customers can connect.
                  The router restarts its services here, so this takes a minute
                  or two.
               </Muted>
               <BarTrack>
                  <BarFill style={{ width: `${configPct}%` }} />
               </BarTrack>
               <Muted>{configPct}%</Muted>
            </Center>
         )}

         {phase === 'replaying' && (
            <Center>
               <Lead style={{ textAlign: 'center', marginBottom: 4 }}>
                  Restoring customer sessions onto the new router…
               </Lead>
               <Muted>
                  Re-granting paid time so customers keep their money. The router
                  restarts its services for a minute or two after setup, so this
                  can take a moment. It keeps going on the server even if you
                  close this window.
               </Muted>
               <BarTrack>
                  <BarFill
                     style={{
                        width: `${replay.total ? (replay.done / replay.total) * 100 : 100}%`,
                     }}
                  />
               </BarTrack>
               <Muted>
                  {replay.done} of {replay.total} restored
               </Muted>
            </Center>
         )}

         {phase === 'done' && result && (
            <Center>
               <BigCheck>✓</BigCheck>
               <Lead style={{ textAlign: 'center' }}>
                  {result.mode === 'restored'
                     ? 'Router restored. Your license is active again.'
                     : `New router linked. Your license moved to it (serial ${result.serial}).`}
               </Lead>
               {(result.sessionReplay?.total ?? 0) > 0 && (
                  <Muted>
                     {replay.done >= replay.total
                        ? `✓ ${replay.total} customer session${replay.total === 1 ? '' : 's'} restored.`
                        : `Restoring ${replay.total} customer session${replay.total === 1 ? '' : 's'} in the background…`}
                  </Muted>
               )}
               <Actions>
                  <Primary onClick={handleClose}>Done</Primary>
               </Actions>
            </Center>
         )}

         {phase === 'error' && (
            <>
               <ErrBox>{errMsg}</ErrBox>
               <Actions>
                  <Ghost onClick={handleClose}>Close</Ghost>
                  <Primary onClick={() => waitForRouter(0)}>Try again</Primary>
               </Actions>
            </>
         )}
      </Wrap>
   );
};

export default ReflashRouter;

function apiErr(err: unknown, fallback: string): string {
   return (
      (err as { response?: { data?: { error?: string } } })?.response?.data
         ?.error || fallback
   );
}

const Wrap = styled.div`
   padding: 4px 2px;
   max-width: 480px;
`;
const Lead = styled.p`
   margin: 0 0 14px;
   font-size: 14px;
   line-height: 1.55;
   color: ${colors.DARK};
`;
const Note = styled.div`
   margin: 0 0 18px;
   padding: 10px 12px;
   border-radius: 8px;
   background: ${colors.WARNING}15;
   border: 1px solid ${colors.WARNING}40;
   font-size: 12.5px;
   line-height: 1.5;
   color: ${colors.DARK};
`;
const FieldLabel = styled.label`
   display: block;
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY};
   margin-bottom: 5px;
`;
const ModelSelect = styled.select`
   width: 100%;
   padding: 9px 12px;
   border: 1px solid #d8d8d8;
   border-radius: 8px;
   font-size: 13px;
   margin-bottom: 8px;
   background: #fff;
   cursor: pointer;
`;
const Muted = styled.p`
   margin: 0;
   font-size: 12.5px;
   color: ${colors.GRAY};
   text-align: center;
`;
const PasteBlock = styled.div`
   margin-bottom: 12px;
`;
const PasteLabel = styled.div`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY};
   margin-bottom: 5px;
`;
const Code = styled.pre`
   margin: 0 0 6px;
   padding: 10px 12px;
   border-radius: 8px;
   background: ${colors.DARK};
   color: #e6edf3;
   font-size: 12px;
   white-space: pre-wrap;
   word-break: break-all;
   max-height: 120px;
   overflow: auto;
`;
const CopyBtn = styled.button`
   border: 1px solid ${colors.PRIMARY};
   background: #fff;
   color: ${colors.PRIMARY};
   border-radius: 6px;
   padding: 4px 12px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
`;
const Actions = styled.div`
   display: flex;
   gap: 10px;
   justify-content: flex-end;
   margin-top: 8px;
`;
const Primary = styled.button`
   border: none;
   border-radius: 8px;
   padding: 10px 18px;
   font-size: 13px;
   font-weight: 600;
   color: #fff;
   background: ${colors.PRIMARY};
   cursor: pointer;
   &:disabled { opacity: 0.55; cursor: not-allowed; }
`;
const Ghost = styled.button`
   border: 1px solid #e0e0e0;
   border-radius: 8px;
   padding: 10px 16px;
   font-size: 13px;
   background: #fff;
   color: ${colors.GRAY};
   cursor: pointer;
`;
const Center = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 12px;
   padding: 12px 0;
`;
const Spinner = styled.div`
   width: 34px;
   height: 34px;
   border-radius: 50%;
   border: 3px solid ${colors.PRIMARY}30;
   border-top-color: ${colors.PRIMARY};
   animation: spin 0.8s linear infinite;
   @keyframes spin {
      to { transform: rotate(360deg); }
   }
`;
const BarTrack = styled.div`
   width: 100%;
   height: 10px;
   border-radius: 6px;
   background: ${colors.PRIMARY}1a;
   overflow: hidden;
   margin: 4px 0 2px;
`;
const BarFill = styled.div`
   height: 100%;
   background: ${colors.PRIMARY};
   border-radius: 6px;
   transition: width 0.4s ease;
`;
const BigCheck = styled.div`
   width: 44px;
   height: 44px;
   border-radius: 50%;
   background: ${colors.SUCCESS};
   color: #fff;
   font-size: 24px;
   font-weight: 700;
   display: flex;
   align-items: center;
   justify-content: center;
`;
const ErrBox = styled.div`
   margin-bottom: 16px;
   padding: 12px 14px;
   border-radius: 8px;
   background: ${colors.DANGER}10;
   border: 1px solid ${colors.DANGER}40;
   font-size: 13px;
   line-height: 1.5;
   color: ${colors.DANGER};
`;
